import { TagEntity } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Path, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Form, FormFieldItem } from '@/components/ui/form';
import { CreatableMultiSelect } from '@/components/ui/multi-select';
import { getTagsQueryOptions } from '@/queries/assets';

const formSchema = z.object({
  tags: z.array(z.object({ label: z.string(), value: z.string() })),
});

function BulkUpdateAssetTagsDialog({
  open,
  onOpenChange,
  callback,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback: (tags: string[], resetForm: () => void) => void;
  loading: boolean;
}) {
  const { data: tags } = useQuery(getTagsQueryOptions({ entity: TagEntity.Asset }));

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tags: [],
    },
  });

  function handleSubmission(data: z.infer<typeof formSchema>) {
    const tags = data.tags.map((tag) => tag.value);
    callback(tags, () => form.reset());
  }

  function handleOpenChange(open: boolean) {
    onOpenChange(open);
    if (!open) {
      form.reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col gap-[10px]">
        <DialogTitle>Edit Asset Tags</DialogTitle>

        <DialogDescription>Bulk editing will change the tags of all the assets you selected.</DialogDescription>

        <Form {...form}>
          <form className="mt-2 flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmission)}>
            <FormFieldItem<z.infer<typeof formSchema>, Path<z.infer<typeof formSchema>>> control={form.control} name="tags" label="Tags" labelSize="small">
              {({ value, onChange }) => (
                <CreatableMultiSelect<{ label: string; value: string }>
                  options={tags?.map(({ tag }) => ({ label: tag, value: tag })) || []}
                  placeholder="Type new tags or select from list..."
                  value={value as { label: string; value: string }[]}
                  onChange={(values) => {
                    onChange((values as { label: string; value: string }[]) || []);
                  }}
                />
              )}
            </FormFieldItem>

            <div className="mt-4">
              <Button variant="success" type="submit" disabled={loading}>
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { BulkUpdateAssetTagsDialog };
